import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { adminApi } from "../../lib/portalApi";
import { toast } from "sonner";
import { Trash2, Upload, Code2, ShieldAlert, CheckCircle2, AlertCircle } from "lucide-react";

const CODE_TYPES = ["html", "css", "js", "ts", "py", "json", "yaml", "md", "sql", "react", "fastapi", "config"];
const DECISIONS = [
  ["approve",            "Approve"],
  ["reject",             "Reject"],
  ["request_revisions",  "Request Revisions"],
  ["deploy_staging",     "Flag: Deploy Staging"],
  ["deploy_production",  "Flag: Deploy Production"],
  ["rollback",           "Flag: Rollback"],
];

const EMPTY = { title: "", summary: "", code_type: "py", filename: "", code_body: "" };

export default function DevBuildTab() {
  const [builds, setBuilds] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [decision, setDecision] = useState({});

  const load = async () => {
    const r = await adminApi.buildsList();
    setBuilds(r.builds || []);
    setStatuses(r.statuses || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await adminApi.buildsCreate(form);
      toast.success("Build submitted for review");
      setForm(EMPTY); setShowForm(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Submit failed"); }
  };

  const act = async (id) => {
    const d = decision[id] || {};
    if (!d.decision) return toast.error("Select a decision");
    try {
      await adminApi.buildsDecision(id, { decision: d.decision, comment: d.comment });
      toast.success(`Build ${d.decision}`);
      setDecision({ ...decision, [id]: {} });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete build submission?")) return;
    try { await adminApi.buildsDelete(id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-devbuild-tab">
      <div className="glass p-4 border-l-2 border-gold flex items-start gap-3">
        <ShieldAlert size={18} className="text-gold shrink-0 mt-0.5" />
        <div className="text-xs text-cream-dim leading-relaxed">
          <span className="text-gold font-semibold">Scaffolded Workflow · No Code Execution.</span>{" "}
          Submissions are static-checked for syntax and risky patterns, stored for review, and transitioned through status flags.
          Real rollouts must be attached to CI/CD — this console never executes code.
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={() => setShowForm(!showForm)} className="btn-gold h-10 px-5 text-xs" data-testid="devbuild-new-btn">
          <Upload size={14} className="mr-2" /> Submit Build
        </Button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="glass p-5 space-y-3" data-testid="devbuild-form">
          <div className="grid md:grid-cols-2 gap-3">
            <Input required placeholder="Title" value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              className="bg-navy-deep border-gold-dim/40" data-testid="devbuild-title" />
            <Input required placeholder="Filename (e.g. utils.py)" value={form.filename}
              onChange={e => setForm({ ...form, filename: e.target.value })}
              className="bg-navy-deep border-gold-dim/40" data-testid="devbuild-filename" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Select value={form.code_type} onValueChange={v => setForm({ ...form, code_type: v })}>
              <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="devbuild-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                {CODE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Summary (optional)" value={form.summary || ""}
              onChange={e => setForm({ ...form, summary: e.target.value })}
              className="bg-navy-deep border-gold-dim/40" />
          </div>
          <Textarea rows={10} required placeholder="Paste your code here"
            value={form.code_body} onChange={e => setForm({ ...form, code_body: e.target.value })}
            className="bg-navy-deep border-gold-dim/40 text-cream font-mono text-sm"
            data-testid="devbuild-body" />
          <div className="flex justify-end gap-2">
            <Button type="button" onClick={() => setShowForm(false)} className="btn-ghost-gold h-10 px-4 text-xs">Cancel</Button>
            <Button type="submit" className="btn-gold h-10 px-5 text-xs" data-testid="devbuild-submit-btn">
              <Code2 size={14} className="mr-2" /> Submit for Review
            </Button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {builds.map(b => {
          const v = b.validation || {};
          const d = decision[b.id] || {};
          return (
            <article key={b.id} className="glass p-5" data-testid={`devbuild-card-${b.version}`}>
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-gold font-mono text-xs">{b.version}</span>
                    <span className="text-[10px] uppercase tracking-wider text-electric">{b.code_type}</span>
                    <span className="text-cream-dim text-xs">{b.filename}</span>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-gold text-gold">{b.status}</span>
                  </div>
                  <h3 className="text-cream text-lg mt-1">{b.title}</h3>
                  {b.summary && <p className="text-cream-dim text-sm mt-1">{b.summary}</p>}
                  <div className="text-[10px] text-steel mt-1">
                    By {b.submitted_by} · {new Date(b.created_at).toLocaleString()}
                  </div>
                </div>
                <button onClick={() => del(b.id)} className="text-[color:var(--auditiq-critical)] hover:text-gold"
                  data-testid={`devbuild-delete-${b.version}`}>
                  <Trash2 size={14} />
                </button>
              </div>

              <div className="grid md:grid-cols-3 gap-2 mt-3 text-xs">
                <div className="flex items-center gap-2">
                  {v.syntax_ok
                    ? <CheckCircle2 size={14} className="text-[color:var(--auditiq-success)]" />
                    : <AlertCircle size={14} className="text-[color:var(--auditiq-critical)]" />}
                  <span className="text-cream-dim">Syntax</span>
                </div>
                <div className="flex items-center gap-2">
                  {(v.risk_flags?.length || 0) === 0
                    ? <CheckCircle2 size={14} className="text-[color:var(--auditiq-success)]" />
                    : <ShieldAlert size={14} className="text-[color:var(--auditiq-critical)]" />}
                  <span className="text-cream-dim">Risk flags ({v.risk_flags?.length || 0})</span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertCircle size={14} className="text-gold" />
                  <span className="text-cream-dim">Manual CI required</span>
                </div>
              </div>

              {(v.issues?.length || v.risk_flags?.length) ? (
                <ul className="mt-2 text-xs text-[color:var(--auditiq-warning)] space-y-1">
                  {(v.issues || []).map((i, idx) => <li key={`i${idx}`}>• {i}</li>)}
                  {(v.risk_flags || []).map((i, idx) => <li key={`r${idx}`} className="text-[color:var(--auditiq-critical)]">⚠ {i}</li>)}
                </ul>
              ) : null}

              <details className="mt-3 text-xs">
                <summary className="cursor-pointer text-electric hover:text-gold">View code</summary>
                <pre className="mt-2 p-3 bg-navy-deep border border-gold-dim/20 rounded overflow-x-auto text-cream-dim max-h-80">
{b.code_body}
                </pre>
              </details>

              {(b.comments?.length || 0) > 0 && (
                <div className="mt-3 border-t border-gold-dim/15 pt-3 space-y-1">
                  {b.comments.map((c, i) => (
                    <div key={i} className="text-xs text-cream-dim">
                      <span className="text-electric">{c.by}</span> — {c.decision}: {c.comment} <span className="text-steel">({new Date(c.at).toLocaleString()})</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Decision */}
              <div className="mt-4 border-t border-gold-dim/15 pt-3 grid md:grid-cols-[200px_1fr_auto] gap-2 items-center">
                <Select value={d.decision || ""} onValueChange={v => setDecision({ ...decision, [b.id]: { ...d, decision: v } })}>
                  <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream"
                    data-testid={`devbuild-decision-${b.version}`}>
                    <SelectValue placeholder="Choose decision" />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                    {DECISIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input placeholder="Comment (optional)" value={d.comment || ""}
                  onChange={e => setDecision({ ...decision, [b.id]: { ...d, comment: e.target.value } })}
                  className="bg-navy-deep border-gold-dim/40" />
                <Button onClick={() => act(b.id)} className="btn-gold h-10 px-4 text-xs"
                  data-testid={`devbuild-act-${b.version}`}>
                  Apply
                </Button>
              </div>
            </article>
          );
        })}
        {!builds.length && <div className="glass p-10 text-center text-cream-dim italic">No build submissions yet.</div>}
      </div>
      <div className="text-[10px] text-steel">
        Available status flags: {statuses.join(" · ")}
      </div>
    </div>
  );
}
